import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, RootDispatch } from "../../../store";
import { fetchRawMaterialsThunk } from "../../../store/slices/warehouseSlice";
import { fetchPaginatedFloorStock } from "../../../store/slices/floorInwardSlice";
import { toast } from "react-hot-toast";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { usePreventWheelChange } from "../../../utils/usePreventWheelChange";
import { UNITS } from "../../../models/warehouseRawMaterial.model";
import { outwardMaterials } from "../../../store/slices/Factory/newFloorSlice";

interface OutwardMaterialPayload {
  requestId: string;
  materialId: string;
  quantity: number;
  unit: string;
}

interface MaterialOption {
  value: string;
  label: string;
  unit: string;
}

interface OutwardMaterialModalProps {
  isOpen: boolean;
  onClose: (success: boolean) => void;
  requestId: string;
  requestNumber: string;
}

const NewOutwardMaterialModal: React.FC<OutwardMaterialModalProps> = ({
  isOpen,
  onClose,
  requestId,
  requestNumber,
}) => {
  const dispatch: RootDispatch = useDispatch();
  const rawMaterials = useSelector(
    (state: RootState) => state.warehouse.rawMaterials || [],
  );
  const isOutwardSubmitting = useSelector(
    (state: RootState) => state.newFloor.loading.outwardMaterialsloading,
  );

  const handleScrollWheelNumberInput = usePreventWheelChange();

  // Single item state instead of an array
  const [formData, setFormData] = useState<OutwardMaterialPayload>({
    requestId,
    materialId: "",
    quantity: 0,
    unit: "piece",
  });

  const fetchRawMaterials = useCallback(
    async (search: string) => {
      try {
        const result = await dispatch(
          fetchRawMaterialsThunk({
            page: 1,
            limit: 20,
            search: search,
            bom: false,
            sortBy: "name",
            sortOrder: "asc",
            from: "factory",
          }),
        ).unwrap();

        return result.data.map((p) => ({
          value: p._id,
          label: p.name,
          unit: p.unit,
        }));
      } catch (err) {
        return [];
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (isOpen) {
      fetchRawMaterials("");
    }
  }, [fetchRawMaterials, isOpen]);

  const updateFormField = (field: keyof OutwardMaterialPayload, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: field === "quantity" ? Number(value) : value,
    }));
  };

  const submitOutwardMaterial = async () => {
    if (!formData.requestId || formData.requestId.trim() === "") {
      toast.error("Floor Request ID is required.");
      return;
    }
    if (!formData.materialId) {
      toast.error("Please select a material.");
      return;
    }
    if (!formData.quantity || formData.quantity <= 0) {
      toast.error("Quantity must be greater than 0.");
      return;
    }

    try {
      // Sends single object payload or wrapped array if backend expects [payload]
      // (Wrapped in an array here in case your slice/controller handles array payloads: [formData])
      const res = await dispatch(
        outwardMaterials({ materials: [formData] }),
      ).unwrap();

      onClose(res.success ?? false);

      // Reset form
      setFormData({
        requestId: "",
        materialId: "",
        quantity: 0,
        unit: "piece",
      });

      dispatch(
        fetchPaginatedFloorStock({
          page: 1,
          limit: 10,
          search: "",
        }),
      );
    } catch (err: any) {
      toast.error(err?.message || "Failed to submit outward material request");
    }
  };

  const customSelectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      padding: "2px",
      fontSize: "0.875rem",
      borderRadius: "0.5rem",
      borderColor: state.isFocused ? "#3B82F6" : "#E2E8F0",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(59, 130, 246, 0.2)" : "none",
      "&:hover": { borderColor: "#CBD5E1" },
      minHeight: "2.5rem",
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
    menu: (base: any) => ({ ...base, zIndex: 99999 }),
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col overflow-hidden border border-slate-100">
        {/* HEADER */}
        <div className="flex-none px-5 py-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-slate-800">
              Outward Material Request
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Fulfill floor request & convert to live stock
            </p>
          </div>
          <button
            onClick={() => onClose(false)}
            className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200/60 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* BODY */}
        <div className="p-5 sm:p-6 space-y-4 overflow-y-auto max-h-[75vh]">
          {/* Form Inputs */}
          <div className="space-y-4">
            {/* Request ID */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">
                Batch Number
              </label>
              <div className="inline-flex items-center rounded-full bg-indigo-100 border border-indigo-300 px-4 py-1.5 text-indigo-700 font-bold text-sm shadow-sm">
                #{requestNumber}
              </div>
            </div>

            {/* Material Async Select */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Material <span className="text-rose-500">*</span>
              </label>
              <AsyncSelect<MaterialOption, false>
                cacheOptions
                defaultOptions
                loadOptions={fetchRawMaterials}
                value={
                  formData.materialId
                    ? rawMaterials
                        .filter((p) => p._id === formData.materialId)
                        .map((p) => ({
                          value: p._id,
                          label: p.name,
                          unit: p.unit,
                        }))[0] || null
                    : null
                }
                onChange={(opt) => {
                  const selectedUnit =
                    opt?.unit ||
                    rawMaterials.find((p) => p._id === opt?.value)?.unit ||
                    "piece";

                  setFormData((prev) => ({
                    ...prev,
                    materialId: opt ? opt.value : "",
                    unit: opt ? selectedUnit : "piece",
                  }));
                }}
                placeholder="Search Material..."
                styles={customSelectStyles}
                menuPortalTarget={document.body}
                isClearable
              />
            </div>

            {/* Quantity and Unit Row */}
            <div className="grid grid-cols-2 gap-3">
              {/* Quantity */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Quantity <span className="text-rose-500">*</span>
                </label>
                <input
                  type="number"
                  onWheel={handleScrollWheelNumberInput}
                  placeholder="0"
                  value={formData.quantity || ""}
                  onChange={(e) => updateFormField("quantity", e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                />
              </div>

              {/* Unit */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Unit
                </label>
                <Select
                  options={UNITS}
                  value={UNITS.find((u) => u.value === formData.unit) || null}
                  isDisabled={true}
                  placeholder="Unit"
                  styles={customSelectStyles}
                  menuPortalTarget={document.body}
                />
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="flex-none p-4 sm:p-5 border-t border-slate-200 bg-white flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
          <button
            onClick={() => onClose(false)}
            className="w-full sm:w-1/2 py-2.5 text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium text-sm"
          >
            Cancel
          </button>
          <button
            onClick={submitOutwardMaterial}
            disabled={isOutwardSubmitting}
            className="w-full sm:w-1/2 py-2.5 text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 transition-all font-medium text-sm flex items-center justify-center gap-2 shadow-sm shadow-blue-500/30"
          >
            {isOutwardSubmitting ? (
              <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              "Submit Request"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewOutwardMaterialModal;
